"use client";

import { useMemo } from "react";

import { NoTranslate } from "@/components/no-translate";
import { useGlobalData } from "@/providers";
import {
  DIGITAL_ASSETS_DISPLAY_DECIMALS,
  getStablecoinDecimals,
  USD_STABLECOINS_REGEX,
} from "@/utils/formatCurrency";
import { formatWholeNumberWithGrouping } from "@/utils/numberFormatting";

const roundToFixed = (num: number, decimals: number) => {
  const factor = Math.pow(10, decimals);
  return (Math.round(num * factor) / factor).toFixed(decimals);
};

interface AnimatedNumberProps {
  value: number | string;
  currency: string;
  className?: string;
}

function AnimatedNumber({ value, currency, className }: AnimatedNumberProps) {
  // Parse the formatted string to extract number and currency parts
  const { numericValue, currencyPart } = useMemo(() => {
    if (typeof value === "number") {
      return {
        numericValue: value,
        currencyPart: "",
      };
    }

    // Handle format like "1,234.56 USD"
    const trimmedValue = value.trim();
    const parts = trimmedValue.split(/\s+/);
    const numericPart = parts[0] || "0";
    const currencyCode = parts[1] || "";

    return {
      numericValue: parseFloat(numericPart.replace(/,/g, "")) || 0,
      currencyPart: currencyCode,
    };
  }, [value]);

  const { products } = useGlobalData();

  const formattedNumber = useMemo(() => {
    if (!currency || !products) {
      return numericValue.toFixed(2);
    }

    const product = products?.find((p) => p.code === currency);
    const isDigitalAsset = product?.decimalPlaces && product?.decimalPlaces > 2;

    if (!product) {
      return numericValue.toFixed(2);
    }

    let formattedAmount: string;

    // USD stablecoins: minimum 2 decimals, but show all significant digits
    if (USD_STABLECOINS_REGEX.test(currency)) {
      const decimals = getStablecoinDecimals(
        numericValue,
        product.decimalPlaces
      );
      formattedAmount = roundToFixed(numericValue, decimals);
    } else if (isDigitalAsset) {
      // If product has more than 2 decimal places we can assume it's a digital asset
      formattedAmount = roundToFixed(
        numericValue,
        DIGITAL_ASSETS_DISPLAY_DECIMALS
      );
    } else {
      // Fiat
      formattedAmount = roundToFixed(numericValue, product.decimalPlaces);
    }

    // Add comma formatting to the amount
    const [wholePart = "0", decimalPart] = formattedAmount.split(".");
    const formattedWholePart = formatWholeNumberWithGrouping(wholePart);
    return decimalPart
      ? `${formattedWholePart}.${decimalPart}`
      : formattedWholePart;
  }, [numericValue, currency, products]);

  return (
    <NoTranslate className={className}>
      {formattedNumber}
      {currencyPart && ` ${currencyPart}`} {currency}
    </NoTranslate>
  );
}

export default AnimatedNumber;
